<script>
import PatientHistory from "@/components/patient/PatientHistory.vue";
import HistorialPrestacional from "@/components/patient/HistorialPrestacional.vue";
import Spinner from "@/components/ui/Spinner";
import VitalBox from "@/components/patient/dashboard/VitalBox";
import LabVitalsTable from "@/components/patient/dashboard/LabVitalsTable.vue";
import AlertBox from "@/components/patient/dashboard/AlertBox";
import ActivityAlertBox from "@/components/patient/dashboard/ActivityAlertBox";
import FormBox from "@/components/patient/dashboard/FormBox";
import IndicationBox from "@/components/patient/dashboard/IndicationBox";
import NotesBox from "@/components/patient/dashboard/NotesBox";
import configService from "@/services/config";
import patientService from "@/services/patient";
import alertsService from "@/services/alerts";
import segmentService from "@/services/segment";
import ModalTaskGraph from "@/components/patient/dashboard/ModalTaskGraph";
import ModalPatientTasksList from "@/components/patient/dashboard/ModalPatientTasksList";
import ModalManualAddTask from "@/components/patient/dashboard/ModalManualAddTask";
import ModalPatientMap from "@/components/patient/dashboard/ModalPatientMap";
import ModalTaskDetail from "@/components/patient/dashboard/ModalTaskDetail";
import ModalOpenEpisode from "@/components/patient/dashboard/ModalOpenEpisode";
import ModalGroup from "@/components/patient/dashboard/ModalGroup";
import ModalPatientEcgTable from "@/components/patient/dashboard/ModalPatientEcgTable";
import PatientLabels from "@/components/patient/dashboard/PatientLabels";
import episodeService from "@/services/episode";
import helperService from "@/services/helper";
import SeguimientoTab from "@/components/patient/dashboard/SeguimientoTab";
import { mapGetters } from "vuex";
import { cloneDeep } from "lodash";
import {
  enableEpisodeCreation,
  isIndicationsEnabled,
  isVideoRoomEnabled,
  isTasksEnabled,
  isMonitoringEnabled,
  isPatientEvolutionEnabled,
} from "@/services/config";
import { get } from "lodash";
import IndicationForm from "@/components/appointment/indications/IndicationForm.vue";
import { INDICATIONS_TYPES } from "@/services/constants";
import indicationService from "@/services/indication";
import { filterPersistibleIndications } from "@/services/indication-filter";
import formService from "@/services/form";
import programPatientService from "@/services/programPatient";
import fileUploadConfigService from "@/services/fileupload-config";
import api from "@/services/api";
import {
  getMedicalInsuranceNumber,
  getMedicalInsurancePlan,
  getMedicalInsuranceName,
} from "@/services/patient-utils";
import { isProtocolsEnabled } from "@/services/config";

export default {
  name: "PatientDashboard",

  components: {
    Spinner,
    VitalBox,
    LabVitalsTable,
    AlertBox,
    ActivityAlertBox,
    ModalTaskGraph,
    ModalPatientTasksList,
    ModalManualAddTask,
    ModalPatientMap,
    ModalTaskDetail,
    ModalOpenEpisode,
    ModalGroup,
    ModalPatientEcgTable,
    PatientLabels,
    SeguimientoTab,
    FormBox,
    IndicationBox,
    NotesBox,
    PatientHistory,
    HistorialPrestacional,
    IndicationForm,
  },

  data() {
    return {
      isProtocolsEnabled,
      isMonitoringEnabled,
      /**
       * @type {boolean} True si el dashboard se renderiza dentro del WebView
       * de la app mobile. Cuando es true, se oculta el header con datos
       * personales del paciente (Vilota, Edad, DNI, etc.) — esa info ya
       * la tiene el paciente en su credencial nativa de la app. La flag la
       * setea el WebView mobile en localStorage antes de cargar la página.
       */
      isInAppWebView: localStorage.getItem("inAppWebView") === "true",
      modal: "",
      selectedType: "",
      selectedTask: null,
      alertsLimit: 6,
      patientId: this.$route.params.id || this.$store.state.user._id,
      patient: {},
      publicGroup: [],
      isLoading: false,
      alerts: [],
      vitals: [],
      activityAlerts: [],
      showDialogModal: false,
      enableEpisodeCreation,
      key: this.$route.path,
      isSalesEnabled: configService.theme === "albya",
      isIndicationsEnabled,
      isVideoRoomEnabled,
      isPatientEvolutionEnabled,
      isTasksEnabled,
      // Tab por defecto. Si el caller envía ?tab=info en la URL, se respeta
      // (ver hook created). Por ejemplo: el tablero de Seguimientos navega
      // con ?tab=info para que el médico aterrice directo en seguimiento del
      // paciente en vez de en historia clínica.
      dashboardTab: "history",
      // Se agrega filter para no mostrar imagesResults y labResults entre las opciones de creación
      forms: cloneDeep(
        INDICATIONS_TYPES.filter(
          (form) => form.type !== "imagesResults" && form.type !== "labResults"
        )
      ),
      savedIndications: [],
      patientIndicationsFilter: [
        "medications",
        "images",
        "lab",
        "practices",
        "notes",
        "certificates",
      ],
      otherIndicationsFilter: ["evolution"],
      isSavingIndications: false,
      isSavingEvolution: false,
      isDownloadingPDF: false,
      /** @description Controla visibilidad del drawer de evolución */
      showEvolutionPanel: false,
      /** @description Lab vitals históricos para paciente (sin filtro de ventana temporal) */
      historicalLabVitals: {},
    };
  },

  created() {
    segmentService.track("Patient Dashboard");
    this.loadInitialData();

    // Si el caller envía ?tab=<info|history|prestacional> en la URL,
    // respetar ese tab al arrancar. Lo usa el tablero de Seguimientos para
    // aterrizar directo en "Seguimiento del paciente" en lugar del default
    // "Historia clínica".
    const tabFromQuery = this.$route.query && this.$route.query.tab;
    if (
      tabFromQuery &&
      ["history", "info", "prestacional"].includes(tabFromQuery)
    ) {
      this.dashboardTab = tabFromQuery;
    }

    this.$bus.$on("updateIndication", (indication, isDeleted = false) => {
      const indicationIndex = this.savedIndications.findIndex(
        (e) => e.key === indication.key
      );
      if (indicationIndex !== -1) {
        if (!isDeleted) {
          this.$set(this.savedIndications, indicationIndex, indication);
        } else {
          this.savedIndications.splice(indicationIndex, 1);
        }
      } else {
        if (!isDeleted) {
          this.savedIndications.push(indication);
        }
      }
    });

    this.$bus.$on("filesUploaded", (indicationKey) => {
      const indicationIndex = this.savedIndications.findIndex(
        (e) => e.key === indicationKey
      );
      if (indicationIndex !== -1) {
        this.$set(this.savedIndications, indicationIndex, {
          ...this.savedIndications[indicationIndex],
          hasFiles: false,
        });
      }

      if (this.savedIndications.every((indication) => !indication.hasFiles)) {
        this.isSavingIndications = false;
      }
    });

    this.$bus.$on("program-form-completed", () => {
      this.$forceUpdate();
    });
  },

  beforeDestroy() {
    this.$bus.$off("program-form-completed");
  },

  computed: {
    ...mapGetters([
      "isAdmin",
      "isAssistant",
      "isDoctor",
      "isPatient",
      "isAdmissionist",
    ]),

    patientGender() {
      switch (this.patient.gender) {
        case "m":
          return "Hombre";

        case "f":
          return "Mujer";

        default:
          return "Otro";
      }
    },

    get() {
      return get;
    },

    /**
     * @description Vitales de laboratorio (displayType table) para mostrar en tabla.
     * Para pacientes usa historicalLabVitals (endpoint sin ventana temporal)
     * para mostrar resultados de cualquier fecha.
     * @returns {Object}
     */
    labVitals() {
      if (this.isPatient) return this.historicalLabVitals;
      const LAB_NAMES = [
        "Hemoglobina (H)",
        "Hemoglobina (M)",
        "Hematocrito (H)",
        "Hematocrito (M)",
        "Glóbulos Blancos",
        "Plaquetas",
        "Glucemia",
        "HbA1c",
        "Urea",
        "Creatinina",
        "Ácido Úrico",
        "Insulina",
        "Colesterol Total",
        "Colesterol HDL (H)",
        "Colesterol HDL (M)",
        "Colesterol LDL",
        "Triglicéridos",
        "GPT (ALAT)",
        "GOT (ASAT)",
        "Bilirrubina Total",
        "TSH",
        "T4 Libre",
        "PSA",
      ];
      return Object.fromEntries(
        Object.entries(this.vitals).filter(([name]) => LAB_NAMES.includes(name))
      );
    },

    /**
     * @description Vitales clínicos (no lab) para mostrar en gráfico
     * @returns {Object}
     */
    clinicalVitals() {
      const LAB_NAMES = [
        "Hemoglobina (H)",
        "Hemoglobina (M)",
        "Hematocrito (H)",
        "Hematocrito (M)",
        "Glóbulos Blancos",
        "Plaquetas",
        "Glucemia",
        "HbA1c",
        "Urea",
        "Creatinina",
        "Ácido Úrico",
        "Insulina",
        "Colesterol Total",
        "Colesterol HDL (H)",
        "Colesterol HDL (M)",
        "Colesterol LDL",
        "Triglicéridos",
        "GPT (ALAT)",
        "GOT (ASAT)",
        "Bilirrubina Total",
        "TSH",
        "T4 Libre",
        "PSA",
      ];
      return Object.fromEntries(
        Object.entries(this.vitals).filter(
          ([name]) => !LAB_NAMES.includes(name)
        )
      );
    },

    canSaveIndications() {
      if (this.isSavingIndications || this.savedIndications.length === 0) {
        return false;
      }

      const hasRegularIndications = this.savedIndications.some(
        (indication) =>
          !indication.createdAt &&
          this.patientIndicationsFilter.includes(indication.type)
      );

      const hasCompletedForms = this.hasCompletedFormsInIndications();

      const hasCompletedPrograms = this.hasCompletedPrograms();

      return hasRegularIndications || hasCompletedForms || hasCompletedPrograms;
    },

    canSaveEvolution() {
      return (
        !this.isSavingIndications &&
        this.savedIndications.length > 0 &&
        this.savedIndications.some((indication) => !indication.createdAt)
      );
    },
  },

  methods: {
    triggerUpload(createdIndication) {
      this.$refs.indicationForm.triggerUpload(createdIndication);
    },

    setDashboardTab(tab) {
      this.dashboardTab = tab;
    },

    /**
     * @description Alterna la visibilidad del drawer de evolución del paciente
     */
    toggleEvolutionPanel() {
      this.showEvolutionPanel = !this.showEvolutionPanel;
    },

    /**
     * @description Para paciente navega a Mi seguimiento → tab lab.
     * Para médico cambia el tab interno del dashboard.
     */
    goToLabTab() {
      if (this.isPatient) {
        this.$router.push({ name: "patient-monitoring" });
        setTimeout(() => {
          this.$bus.$emit("seguimiento-go-to-lab");
        }, 300);
      } else {
        this.dashboardTab = "info";
        setTimeout(() => {
          this.$bus.$emit("seguimiento-go-to-lab");
        }, 100);
      }
    },

    isHighlighted(tab) {
      return tab === this.dashboardTab ? "selected" : "not-selected";
    },

    getMedicalInsuranceNumber,
    getMedicalInsurancePlan,
    getMedicalInsuranceName,
    /**
     * @description Carga datos iniciales del paciente y sus alertas.
     *              Para pacientes logueados usa getMe (sin toast de error).
     */
    async loadInitialData() {
      this.isLoading = true;

      try {
        const query = {
          scheduledAt: { $lte: new Date() },
        };

        const patientPromise = this.isPatient
          ? patientService.getMe(this.patientId)
          : patientService.getById(this.patientId);

        const alertsPromise = this.isPatient
          ? alertsService
              .getAllAlerts({ patientID: this.patientId, query, silent: true })
              .catch(() => [])
          : alertsService.getAllAlerts({ patientID: this.patientId, query });

        // Para paciente: obtener lab vitals históricos sin filtro de ventana temporal
        const historicalLabPromise = this.isPatient
          ? api
              .get("/tasks/vitals/type/patient/history", {
                params: { sort: { completedAt: -1 } },
                getErrorMessage: () => null,
              })
              .then((res) => res.data?.docs || [])
              .catch(() => [])
          : Promise.resolve([]);

        const [patient, alerts, historicalDocs] = await Promise.all([
          patientPromise,
          alertsPromise,
          historicalLabPromise,
        ]);

        this.patient = patient;
        this.$bus.$emit("update-title", helperService.getFullName(patient));
        this.activityAlerts = alertsService.getActivityAlerts({ alerts });
        const { vitals, rest } = alertsService.separateAlerts({ alerts });
        this.vitals = alertsService.groupBy({
          alerts: vitals,
          limit: this.alertsLimit,
        });
        this.alerts = alertsService.groupBy({
          alerts: rest,
          limit: this.alertsLimit,
          prop: "type",
        });

        // UC-REG-083 → UC-REG-085: el auto-mark al entrar al paciente
        // quedó descartado porque no daba feedback visual al médico
        // (las alertas seguían visibles, el contador seguía igual).
        // Ahora el SeguimientoTab.vue tiene un botón explícito "Marcar
        // todas como vistas" que el médico activa cuando quiere.

        // Agrupar lab vitals históricos por nombre (sin límite temporal) para paciente
        if (historicalDocs.length > 0) {
          this.historicalLabVitals = alertsService.groupBy({
            alerts: historicalDocs.map((d) => ({ ...d, type: "vitals" })),
            limit: this.alertsLimit,
          });
        }

        if (this.patient.patient?.publicGroupId) {
          this.publicGroup = await patientService.getPublicGroupById(
            this.patient.patient.publicGroupId
          );
        }
      } catch (error) {
        if (this.isPatient) {
          console.warn("Error al cargar datos del paciente:", error);
        } else {
          console.error("Error al cargar datos del paciente:", error);
        }
      } finally {
        this.isLoading = false;
      }
    },

    showModal({ modal, task, type, appointments }) {
      this.modal = modal;
      this.selectedTask = task;
      this.selectedType = type;
      this.appointments = appointments;
      this.showDialogModal = true;
    },

    closeModal() {
      this.modal = "";
      this.selectedType = null;
      this.selectedTask = null;
      this.showDialogModal = false;
    },

    onSaveManualTask(task) {
      this.closeModal();
      this.vitals[task.name].unshift(task);
      if (this.vitals[task.name].length > this.alertsLimit) {
        this.vitals[task.name].pop();
      }
    },

    async openEpisode(doctor) {
      try {
        await episodeService.create(this.patientId, {
          doctor,
        });
        this.closeModal();
      } catch (error) {
        //
      }
    },

    getIMC(weight, height) {
      return (weight / (height / 100) ** 2).toFixed(1);
    },

    getFullName(user) {
      return helperService.getFullName(user);
    },

    hasCompletedFormsInIndications() {
      try {
        const indicationFormRef = this.$refs.indicationForm;
        if (!indicationFormRef) return false;

        const allIndicationRefs = indicationFormRef.$refs;

        for (const refName of Object.keys(allIndicationRefs)) {
          if (refName.startsWith("indications-")) {
            const indicationComponent = allIndicationRefs[refName];
            if (indicationComponent?.[0]?.completedForms) {
              const completedForms = indicationComponent[0].completedForms;
              if (Object.keys(completedForms).length > 0) {
                return true;
              }
            }
          }
        }

        return false;
      } catch (error) {
        console.error("Error al verificar formularios completados:", error);
        return false;
      }
    },

    hasCompletedPrograms() {
      try {
        const indicationFormRef = this.$refs.indicationForm;
        if (!indicationFormRef) return false;

        const allIndicationRefs = indicationFormRef.$refs;

        for (const refName of Object.keys(allIndicationRefs)) {
          if (refName.startsWith("indications-")) {
            const indicationComponent = allIndicationRefs[refName];

            if (indicationComponent?.[0]?.completedProgramForms) {
              const completedProgramForms =
                indicationComponent[0].completedProgramForms;

              for (const indicationKey of Object.keys(completedProgramForms)) {
                const programForms = completedProgramForms[indicationKey];

                for (const programId of Object.keys(programForms)) {
                  const formsToSend = programForms[programId];

                  if (formsToSend && formsToSend.length > 0) {
                    return true;
                  }
                }
              }
            }
          }
        }

        return false;
      } catch (error) {
        console.error("Error al verificar programas completados:", error);
        return false;
      }
    },

    async saveIndications(typeFilter = null) {
      try {
        const validIndications = filterPersistibleIndications(
          this.savedIndications,
          typeFilter
        );

        if (validIndications.length > 0) {
          await indicationService.saveIndications(
            validIndications,
            this.patient._id,
            undefined, // No appointmentId en PatientDashboard
            typeFilter,
            this.$store,
            {
              isSavingIndications: this.isSavingIndications,
              onTriggerUpload: this.triggerUpload.bind(this),
              onUpdateIndication: (updatedIndication) => {
                const index = this.savedIndications.findIndex(
                  (ind) => ind.key === updatedIndication.key
                );
                if (index !== -1) {
                  this.$set(this.savedIndications, index, updatedIndication);
                }
              },
              onSetSavingStatus: (status) => {
                this.isSavingIndications = status;
              },
            }
          );
        }

        await this.sendCompletedForms(typeFilter);

        this.$message.success("Indicaciones guardadas con éxito");
      } catch (error) {
        console.error("Error al guardar indicaciones:", error);
        const errorMessage =
          error.response?.data?.error || "Error al enviar las indicaciones";
        this.$message.error(errorMessage);
      }
    },

    async sendCompletedForms() {
      try {
        const indicationFormRef = this.$refs.indicationForm;
        if (!indicationFormRef) return;

        const allIndicationRefs = indicationFormRef.$refs;

        for (const refName of Object.keys(allIndicationRefs)) {
          if (refName.startsWith("indications-")) {
            const indicationComponent = allIndicationRefs[refName];

            if (indicationComponent?.[0]?.completedForms) {
              const completedForms = indicationComponent[0].completedForms;

              for (const indicationKey of Object.keys(completedForms)) {
                const formsToSend = completedForms[indicationKey];

                for (const completedForm of formsToSend) {
                  if (!completedForm.isSent) {
                    await this.sendFormToBackend(
                      completedForm,
                      indicationKey,
                      indicationComponent[0]
                    );
                  }
                }
              }
            }

            if (indicationComponent?.[0]?.completedProgramForms) {
              const completedProgramForms =
                indicationComponent[0].completedProgramForms;

              for (const indicationKey of Object.keys(completedProgramForms)) {
                const programForms = completedProgramForms[indicationKey];

                for (const programId of Object.keys(programForms)) {
                  const formsToSend = programForms[programId];

                  if (formsToSend && formsToSend.length > 0) {
                    await this.sendProgramToBackend(
                      programId,
                      formsToSend,
                      indicationKey,
                      indicationComponent[0]
                    );
                  }
                }
              }
            }
          }
        }
      } catch (error) {
        console.error("Error al enviar formularios completados:", error);
        throw error;
      }
    },

    async sendFormToBackend(completedForm, indicationKey, indicationComponent) {
      try {
        const formData = {
          form: completedForm.form?._id || completedForm._id,
          patient: this.patient._id,
          sections: completedForm.sections || [],
          answeredAt: completedForm.answeredAt || new Date(),
          status: completedForm.status || "open",
          createdBy: this.$store.state.user,
        };

        const createdForm = await formService.postPatientForm(formData);

        completedForm.isSent = true;
        completedForm._id = createdForm._id;
        completedForm.sentAt = new Date();

        if (completedForm.hasFiles && completedForm.dropzoneFiles?.length > 0) {
          await this.uploadFormFiles(
            createdForm._id,
            completedForm.dropzoneFiles
          );
        }

        this.$set(indicationComponent.completedForms, indicationKey, [
          ...indicationComponent.completedForms[indicationKey],
        ]);
      } catch (error) {
        console.error("Error al enviar formulario:", error);
        const errorMessage =
          error.response?.data?.error || "Error al enviar el formulario";
        this.$message.error(errorMessage);
        throw error;
      }
    },

    async uploadFormFiles(formId, files) {
      try {
        const formData = new FormData();

        files.forEach((file) => {
          const nativeFile = file.file || file;
          if (nativeFile instanceof File || nativeFile instanceof Blob) {
            formData.append("files", nativeFile, nativeFile.name);
          }
        });

        const uploadUrl = fileUploadConfigService.getFormFilesEndpoint(formId);
        await api.put(uploadUrl, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        this.$message.success("Archivos adjuntos subidos exitosamente");
      } catch (error) {
        console.error("Error al subir archivos del formulario:", error);
        this.$message.error(
          `Error al subir archivos: ${
            error.response?.data?.error || error.message
          }`
        );
      }
    },

    async sendProgramToBackend(programId, completedForms) {
      try {
        const completedFormsData = completedForms.map((completedForm) => ({
          name:
            completedForm.name ||
            completedForm.form?.name ||
            "Formulario del Programa",
          description:
            completedForm.description ||
            completedForm.form?.description ||
            "Formulario completado del programa",
          sections: completedForm.sections || [],
          status: "closed",
          answeredAt:
            completedForm.answeredAt ||
            completedForm.createdAt ||
            new Date().toISOString(),
        }));

        const programData = {
          program: programId,
          patient: this.patient._id,
          status: "pending",
          completedForms: completedFormsData,
        };

        const result = await programPatientService.assignProgramToPatient(
          programData
        );

        if (result.initialData && Array.isArray(result.initialData)) {
          for (let i = 0; i < completedForms.length; i++) {
            const completedForm = completedForms[i];
            const createdForm = result.initialData[i];

            if (
              completedForm.hasFiles &&
              completedForm.dropzoneFiles?.length > 0 &&
              createdForm?._id
            ) {
              await this.uploadFormFiles(
                createdForm._id,
                completedForm.dropzoneFiles
              );
            }
          }
        }

        completedForms.forEach((completedForm) => {
          completedForm.isSent = true;
          completedForm.sentAt = new Date();
        });
      } catch (error) {
        console.error("Error al enviar programa al backend:", error);
        const errorMessage =
          error.response?.data?.error || "Error al asignar el programa";
        this.$message.error(errorMessage);
        throw error;
      }
    },
    async downloadPDF() {
      try {
        this.isDownloadingPDF = true;
        const response = await patientService.generatePDF(this.patientId);

        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const patientName =
          this.getFullName(this.patient).replace(/[, ]+/g, "-").toLowerCase() ||
          this.patientId;
        link.download = `historia-clinica-${patientName}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.$message.success("PDF descargado correctamente");
      } catch (error) {
        console.error("Error al descargar PDF:", error);

        let errorMessage =
          "Error al descargar el PDF. Por favor, intente nuevamente.";

        if (error.response?.data instanceof Blob) {
          try {
            const text = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsText(error.response.data);
            });
            const errorData = JSON.parse(text);
            errorMessage = errorData.error || errorMessage;
          } catch (parseError) {
            console.warn(
              "No se pudo parsear el error del servidor:",
              parseError
            );
          }
        } else if (error.message) {
          errorMessage = error.message;
        }

        this.$message.error(errorMessage);
      } finally {
        this.isDownloadingPDF = false;
      }
    },
  },

  watch: {
    "$route.params.id": {
      handler: function () {
        this.patientId = this.$route.params.id || this.$store.state.user._id;
        this.loadInitialData();
      },
      immediate: true,
    },
  },
};
</script>

<template lang="pug">
section.patient-dashboard
  spinner(v-if="isLoading")
  .data(v-else)
    .patient-dashboard__back(
      v-if="$route.query.back === 'audit'"
      @click="$router.push({ name: 'audit-detail', params: { id: $route.query.auditId } })"
    )
      i.el-icon-arrow-left
      span Volver a Auditoría
    .whiteBox(v-if="patient && !isInAppWebView")
      .patient-header-container.top
        .patient-header-top
          h1 {{ getFullName(patient) }}
        .patient-header-top
          router-link.button-blue-new(v-if="!isPatient" :to="{ name: 'update-patient', params: { id: patientId } }") Editar paciente
          button.button-blue-new(v-if="publicGroup.length > 1" @click="showModal({modal: 'group'})") Grupo Familiar
          button.button-blue-new(v-show="isDoctor || isAdmin || isAdmissionist" @click="downloadPDF" :disabled="isDownloadingPDF")
            span Descargar PDF
            spinner(v-if="isDownloadingPDF" size="8" style="margin-left: 8px;")
          //- UC-REG-089: "Ver Tareas" y "Asignación de protocolos" tenían
          //- `dashboardTab === 'info'` como condición. Con el rediseño el
          //- default es `history`, por lo que estos botones desaparecían al
          //- entrar al paciente — Federico no encontraba cómo cargar
          //- protocolos. Son acciones globales del paciente, las mostramos
          //- en todos los tabs.
          router-link.button-blue-new(:to="{ name: 'patient-tasks' }" v-if="isTasksEnabled && !isPatient") Ver Tareas
          router-link.button-blue-new(
            v-if="!isPatient && isProtocolsEnabled"
            :to="{ name: 'assign-treatment-patient', params: { id: this.patientId } }"
          ) Asignación de protocolos
          router-link.button-blue-new(v-if='this.isSalesEnabled && this.isDoctor' :to="{name: 'sales-list', params: {patient : this.patient}}") Catálogo de Ventas
          button.button-teal-new(v-if="!isPatient && !isAdmissionist && isPatientEvolutionEnabled" @click="toggleEvolutionPanel")
            span Evolucionar paciente
      .patient-header-container
        .patient-header-bottom
          p.secondary-text Edad
          p.primary-text {{ patient.bornAt | age }}
        .patient-header-bottom
          p.secondary-text DNI
          p.primary-text {{ get(patient, 'governmentId.number', 'N/A') }}
        .patient-header-bottom
          p.secondary-text Teléfono
          p.primary-text {{ patient.phone || 'N/A' }}
        .patient-header-bottom
          p.secondary-text E-mail
          p.primary-text {{ patient.email }}
        .patient-header-bottom
          p.secondary-text Financiador
          p.primary-text {{ getMedicalInsuranceName(patient) }}
        .patient-header-bottom
          p.secondary-text Plan
          p.primary-text {{ getMedicalInsurancePlan(patient) }}
        .patient-header-bottom
          p.secondary-text # de socio
          p.primary-text {{ getMedicalInsuranceNumber(patient) }}

    .container-boxes
      .whiteBox.left
        //- Para pacientes: sin tabs, historia clínica directo
        //- Para admin/doctor: tabs con seguimiento
        .tabs(v-if="!isPatient && isMonitoringEnabled")
          p(:class="isHighlighted('history')" @click="setDashboardTab('history')") Historia clínica
          p(:class="isHighlighted('info')" @click="setDashboardTab('info')") Seguimiento del paciente
          p(v-if="isAdmin" :class="isHighlighted('prestacional')" @click="setDashboardTab('prestacional')") Historial prestacional
        patient-labels(v-show="!isPatient && !isDoctor && dashboardTab === 'info'" v-model="patient.labels" :patientId="patient._id")
        seguimiento-tab(
          v-if="!isPatient"
          v-show="dashboardTab === 'info'"
          :patient="patient"
          :vitals="clinicalVitals"
          :alerts="alerts"
          :activityAlerts="activityAlerts"
          :labVitals="labVitals"
          @show-modal="showModal"
          @refresh-alerts="loadInitialData"
        )
        //- Componentes legacy ocultos (necesarios para refs de modales)
        .columns(v-show="false")
          .top.rest-container
            form-box(v-if="!isPatient && !isDoctor" :patient="patient")
            indication-box(v-if='isIndicationsEnabled' :patient="patient")
            activity-alert-box(v-if="!isPatient && !isDoctor" :alerts="activityAlerts")
          .bottom.rest-container
            alert-box(
              v-if="Object.keys(alerts).length",
              v-for="(alert, key) in alerts",
              @show-modal="showModal",
              :ref="key"
              :key="key",
              :type="key",
              :alerts="alert"
            )
            vital-box(
              v-for="(vital, key) in vitals",
              @show-modal="showModal",
              :ref="key"
              :key="key",
              :name="key",
              :vitals="vital"
              :patient="patient"
            )
        patient-history(
          v-show="isPatient || dashboardTab === 'history'"
          :patientId="patientId"
          :labVitals="labVitals"
          @go-to-vitals="goToLabTab"
        )
        historial-prestacional(
          v-if="isAdmin && dashboardTab === 'prestacional'"
          :patientId="patientId"
          :memberId="patient.member || null"
        )
    .evolution-overlay(v-if="showEvolutionPanel" @click.self="toggleEvolutionPanel")
      .evolution-drawer(:class="{ open: showEvolutionPanel }")
        .evolution-drawer__header
          h2 Evolución del paciente
          button.evolution-drawer__close(@click="toggleEvolutionPanel") ✕
        .evolution-drawer__body
          .buttons
            button.button-blue-new.wide(
              @click="saveIndications(patientIndicationsFilter)"
              :disabled="!canSaveIndications || isSavingIndications"
            ) Enviar prescripciones
            button.button-red-new.wide(
              @click="saveIndications(otherIndicationsFilter)"
              :disabled="!canSaveEvolution || isSavingIndications"
            ) Agregar evolución
          indication-form(
            ref="indicationForm"
            :forms="forms"
            :savedIndications="savedIndications"
            :patient="patient"
          )
      

    modal-task-graph(
      v-if="modal === 'task-graph'",
      :patientId="patientId",
      :name="selectedTask",
      :showDialogModal="showDialogModal",
      @close="closeModal"
    )
    modal-patient-tasks-list(
      v-if="modal === 'tasks-table'",
      :patientId="patientId",
      :name="selectedTask",
      :type="selectedType",
      :showDialogModal="showDialogModal",
      @close="closeModal"
    )
    modal-manual-add-task(
      v-if="modal === 'add-manual-task'",
      :newTask="selectedTask",
      :patientId="patientId",
      :showDialogModal="showDialogModal",
      @close="closeModal",
      @onSave="onSaveManualTask"
    )
    modal-patient-map(
      v-if="modal === 'map'",
      :newTask="selectedTask",
      :showDialogModal="showDialogModal",
      @close="closeModal"
    )
    modal-task-detail(
      v-if="modal === 'task-detail'",
      :task="selectedTask",
      :showDialogModal="showDialogModal",
      @close="closeModal"
    )
    modal-open-episode(
      v-if="modal === 'open-episode'",
      :patientId="patientId",
      :showDialogModal="showDialogModal",
      @close="closeModal"
      @openEpisode="openEpisode"
    )
    modal-group(
      v-if="modal === 'group'",
      :publicGroup="publicGroup"
      :patientId="patient._id"
      :showDialogModal="showDialogModal",
      @close="closeModal"
    )
    modal-patient-ecg-table(
      v-if="modal === 'ecg-table'",
      :selectedTask="selectedTask",
      :showDialogModal="showDialogModal",
      @close="closeModal"
    )
</template>

<style lang="scss" scoped>
// Importar tools/media para usar @include media(breakpoint) — BUG-VQBHY2.
// El prependData de vue.config.js solo trae settings/colors + themes; el
// resto se importa explícito por componente (patrón de CommissionList.vue,
// MedicalSpecialtyList.vue, etc.).
@import "@/styles/settings/index.scss";
@import "@/styles/tools/index.scss";

.headline {
  margin-bottom: 0;
  padding: 5px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  .patient-header {
    display: flex;
    flex-direction: row;
    gap: 5px;

    .patient-details {
      display: grid;
      grid-gap: 15px;
      grid-template-columns: auto auto;
    }
  }

  .headline__buttons {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 5px;
  }
}

.container-boxes {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 20px;

  .left {
    width: 100%;
    padding-top: 8px;
    /* Bug raíz UC-REG-052: por default `min-width: auto` en flex children
       hace que el item crezca para acomodar su contenido aunque tenga
       `width: 100%`. Si dentro hay un .ph-type-row-title con texto largo
       (caso típico: evolución clínica con descripción de varias líneas),
       el child empuja el ancho de toda la página rompiendo el layout.
       `min-width: 0` permite que el flex item se shrink al `width: 100%`
       declarado y respete el truncado con ellipsis del title interno. */
    min-width: 0;
  }
}

.evolution-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1000;
}

.evolution-drawer {
  position: fixed;
  top: 0;
  right: -460px;
  width: 460px;
  height: 100%;
  background: #fff;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
  transition: right 0.3s ease;
  z-index: 1001;
  overflow-y: auto;

  &.open {
    right: 0;
  }
}

.evolution-drawer__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  border-bottom: 1px solid #e8e8e8;

  h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
  }
}

.evolution-drawer__close {
  background: none;
  border: none;
  font-size: 22px;
  cursor: pointer;
  color: #666;
  padding: 4px 8px;
  line-height: 1;

  &:hover {
    color: #333;
  }
}

.evolution-drawer__body {
  padding: 15px 25px;
}

.button-teal-new {
  background-color: #0d9d9d;
  color: #fff;
  border: none;
  // Bumped from 6 → 10 para no quedar tan cuadrado en el header del paciente
  // (resto de la app no se ve afectada: este selector solo aplica acá).
  border-radius: 10px;
  padding: 8px 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: darken(#0d9d9d, 8%);
  }
}

// Override local de los .button-blue-new del header del paciente
// (no toca el global de styles/objects/buttons.scss para no afectar otros
// lugares). Más radius + box-shadow sutil para que no se vean cuadrados.
.patient-header-container.top .button-blue-new,
.patient-header-container.top .button-teal-new {
  border-radius: 10px;
  font-weight: 600;
  letter-spacing: 0.1px;
}

.buttons {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin: 35px 0 20px 0;

  button {
    width: 48%;
  }
}

.columns > .box {
  margin-bottom: 0;
}

.whiteBox {
  width: 100%;
  padding: 16px 20px 10px;
  margin-bottom: 20px;
  background-color: $white;
  border-radius: 10px;
  box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.1);

  .secondary-text {
    font-size: 13px;
    margin: 2px 0;
  }

  .primary-text {
    color: $primary;
    font-weight: 600;
    margin: 2px 0;
  }

  .tabs {
    display: flex;
    flex-direction: row;
    align-items: center;
    text-align: center;
    margin-bottom: 16px;
  }

  .patient-header-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;

    .patient-header-bottom {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
    }

    /* BUG-VQBHY2: en breakpoints ≤ipad (800px) los 7 datos del paciente
       (edad/DNI/tel/email/financiador/plan/# socio) se squeezean y quedan
       ilegibles. Activamos flex-wrap + row-gap para que floten a múltiples
       líneas conservando legibilidad. */
    @include media(ipad) {
      flex-wrap: wrap;
      row-gap: 12px;
      column-gap: 16px;
      justify-content: flex-start;

      .patient-header-bottom {
        flex: 0 0 calc(33.333% - 16px);
        min-width: 0;
      }
    }

    @include media(tablet) {
      .patient-header-bottom {
        flex: 0 0 calc(50% - 16px);
      }
    }
  }

  .patient-header-container.top {
    padding-bottom: 8px;
    margin-bottom: 12px;
    border-bottom: solid 1px #00000024;

    .patient-header-top {
      display: flex;
      justify-content: center;
      align-items: center;

      h1 {
        margin: 0;
        /* Override del h1 global (20px) — bajamos 1pt para que el nombre
           del paciente no compita visualmente con el sidebar y otros
           títulos de la app. Solo aplica al header del paciente. */
        font-size: 19px;
      }

      .button-blue-new,
      .button-teal-new {
        padding: 6px 12px;
        font-size: 12px;
      }
    }

    /* BUG-VQBHY2: en mobile/ipad el nombre + los 7 botones no entran en
       una línea. Stackeamos vertical: nombre arriba, botones flex-wrap
       debajo, alineados a la izquierda. */
    @include media(ipad) {
      flex-direction: column;
      align-items: stretch;
      gap: 10px;

      .patient-header-top {
        flex-wrap: wrap;
        justify-content: flex-start;
        gap: 6px;
      }
    }
  }
}

.patient-attention-header {
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.selected {
  cursor: pointer;
  color: #2a5d9f;
  font-weight: 600;
  border-bottom: solid 2px #2a5d9f;
  width: 100%;
  padding-bottom: 10px;
  margin-right: 2px;
}

.not-selected {
  cursor: pointer;
  color: #333;
  font-weight: 600;
  border-bottom: solid 2px rgba(0, 0, 0, 0.153);
  width: 100%;
  padding-bottom: 10px;
  margin-left: 2px;
}

/* Tablets (iPad portrait, ~1024px y menos) — datos en 4 columnas para
   no quedar tan apretado, botones del header con tamaño cómodo. */
@media (max-width: 1024px) {
  .patient-header-container {
    flex-wrap: wrap;
    gap: 12px;
  }
  .patient-header-container .patient-header-bottom {
    flex: 0 0 calc(25% - 12px);
    min-width: 0;
    overflow: hidden;
    .primary-text {
      overflow-wrap: anywhere;
      word-break: break-word;
    }
  }
  .patient-header-container.top .patient-header-top {
    flex-wrap: wrap;
    gap: 8px;
    .button-blue-new,
    .button-teal-new {
      padding: 8px 14px;
      font-size: 13px;
    }
  }
}

@media (max-width: 768px) {
  .whiteBox {
    padding: 12px 14px 8px;
  }

  /* Header del paciente — adaptación mobile.
     Cambios respecto al desktop:
     - Datos del paciente (Edad, DNI, etc.) en grid de 2 columnas
       en lugar de fila con justify-content: space-between (que
       desbordaba con 7 items + textos largos como email).
     - Botones del header en columna full-width para que sean
       fáciles de tocar y no se desborden.
     - Email con overflow-wrap para que no rompa el layout cuando
       el dominio es largo. */

  .patient-header-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px 12px;
  }

  .patient-header-container.top {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding-bottom: 12px;

    .patient-header-top {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      justify-content: flex-start;

      h1 {
        font-size: 18px;
        width: 100%;
        margin-bottom: 4px;
      }

      .button-blue-new,
      .button-teal-new {
        padding: 8px 12px;
        font-size: 12px;
        flex: 1 1 auto;
        white-space: nowrap;
        border-radius: 10px;
      }
    }
  }

  .patient-header-container .patient-header-bottom {
    flex: 0 0 auto;
    min-width: 0;
    overflow: hidden;

    .secondary-text {
      font-size: 11px;
      color: #6b7280;
    }
    .primary-text {
      font-size: 12px;
      overflow-wrap: anywhere;
      word-break: break-word;
    }
  }

  .evolution-drawer {
    width: 100%;
    right: -100%;
  }
}

/* Mobile chico (smartphones) — 1 columna para que email + financiador
   no se cropeen. Botones del header full-width. */
@media (max-width: 480px) {
  .patient-header-container {
    grid-template-columns: 1fr;
  }

  .patient-header-container.top {
    .patient-header-top {
      h1 {
        font-size: 17px;
      }

      .button-blue-new,
      .button-teal-new {
        flex: 1 1 100%;
        padding: 8px 10px;
        font-size: 12px;
      }
    }
  }
}

.patient-dashboard__back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #002d5e;
  cursor: pointer;
  margin-bottom: 12px;
  padding: 6px 0;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.7;
  }
}
</style>
